import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { get } from "lodash"
import { Creators as IntradayCreators } from "model/intraday"
import { generateIntradayBatchUrl } from "helpers/intraday"
import { useAccountAlias, useAccountId } from "resources/account"
import { DataStatus } from "helpers/data-statuses"
import { Uuid } from "helpers/types/uuid"
import { getToday } from "helpers/types/dates"
import { IntradayLastBatch, IntradayQueryKey, IntradayQueryRecord, ReduxState } from "model/state"
import useResource from "./resources"

const INTRADAY_RESOURCE = "intradayLastBatch"
const EMPTY_OBJECT: Record<IntradayQueryKey, IntradayQueryRecord> = {}

export function useIntradayChannel(useIntraday: boolean, portfolioId: Uuid, viewName: string) {
  const intradayQueries = useSelector((state: ReduxState) => get(state, "global.intraday.queries", EMPTY_OBJECT))
  const dispatch = useDispatch()
  const currentDate = getToday()
  const accountId = useAccountId()
  const accountAlias = useAccountAlias()

  useEffect(() => {
    if (!useIntraday || !accountId || !portfolioId) {
      dispatch(IntradayCreators.unregisterModuleQueries(viewName))
      return
    }

    // id is the same as the url since we're not using graphql
    const id = generateIntradayBatchUrl(accountAlias, portfolioId, currentDate)
    const channel: IntradayQueryKey = `${accountId}_${portfolioId}`
    const currentId = get(intradayQueries, [channel, INTRADAY_RESOURCE, "id"], null)

    if (currentId !== id) {
      if (id) {
        dispatch(IntradayCreators.registerQuery(viewName, channel, INTRADAY_RESOURCE, { url: id, id }))
      } else {
        dispatch(IntradayCreators.unregisterQuery(viewName, channel, INTRADAY_RESOURCE))
      }
    }
  }, [viewName, dispatch, intradayQueries, useIntraday, accountId, portfolioId, currentDate, accountAlias])

  // This needs to be a separate hook as we ONLY want to unregister if viewName changes
  // Unregistering otherwise will create an infinite loop.
  useEffect(() => {
    return () => {
      dispatch(IntradayCreators.unregisterModuleQueries(viewName))
    }
  }, [dispatch, viewName])
}

/**
 * Hook to get the last intraday batch data for a specific portfolio
 */
export function useLastIntradayBatch(portfolioId?: Uuid): [IntradayLastBatch | null, DataStatus] {
  const accountAlias = useAccountAlias()
  const currentDate = getToday()
  return useResource({
    resource: "intradayLastBatch",
    url: () => portfolioId ? generateIntradayBatchUrl(accountAlias, portfolioId, currentDate) : null,
  }, "data")
}
